import React, { useState, useEffect } from 'react';
import { Typography, message } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import { getOwnerDashboardStats, OwnerDashboardStats } from '../../services/statsApi';
import QuickStartActions from './owner/QuickStartActions';
import NeedsAttentionPanel from './owner/NeedsAttentionPanel';
import EstimatesActionPanel from './owner/EstimatesActionPanel';
import ApprovalReviewPanel from './owner/ApprovalReviewPanel';
import PartsStatusPanel from './owner/PartsStatusPanel';
import KpiStrip from './owner/KpiStrip';
import ShopStatusRow from './owner/ShopStatusRow';
import PipelineBar from './owner/PipelineBar';

const { Title, Text } = Typography;

const ZERO_PIPELINE = {
  new: 0, scheduled: 0, in_progress: 0, waiting_parts: 0, waiting_approval: 0, completed: 0,
};

const OwnerDashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<OwnerDashboardStats | null>(null);

  useEffect(() => {
    let active = true;
    getOwnerDashboardStats()
      .then((data) => { if (active) setStats(data); })
      .catch((err) => {
        console.error('Failed to load dashboard stats:', err);
        message.error('Failed to load dashboard statistics');
      });
    return () => { active = false; };
  }, []);

  return (
    <div style={{ padding: '16px 12px', maxWidth: '100%', boxSizing: 'border-box' }}>
      {/*
        A GRID with `minmax(0, 1fr)`, not a Space.

        The vertical Space's items sized themselves to their widest content — 429px
        inside a 350px parent on a 390px screen — and dragged the whole page with them,
        which is how New Work Order, Send, Set ETA and seven other controls ended up
        51px past the right edge of a phone. The page never scrolled sideways while it
        happened, because the containers clip: `body.scrollWidth` stayed clean and the
        buttons were simply cut in half.

        `minmax(0, 1fr)` is the fix that holds: a grid track that is allowed to be
        narrower than its contents. Every row here now stops at the screen, and anything
        genuinely too wide has to deal with its own overflow rather than the page's.
      */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 24 }}>
        <div>
          <Title level={2} style={{ marginBottom: 0 }}>Owner Dashboard</Title>
          <Text type="secondary">Welcome back, {user?.first_name} — here's what needs you today.</Text>
        </div>

        <QuickStartActions />

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 16 }}>
          {/*
            `min(320px, 100%)` rather than a bare 320px: a fixed minimum cannot shrink
            below itself, so on a 342px-wide phone body the track stayed 320px wide and
            the panels' own padding pushed the rest past the edge.
          */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: 16 }}>
            <NeedsAttentionPanel />
            <EstimatesActionPanel />
            {/*
              Spec §10.1: the stalled-approvals count ships WITH the gate. The
              requirement gate is on by default, so without a visible count the
              shop finds out approvals are blocked from a quiet week.
            */}
            <ApprovalReviewPanel />
            <PartsStatusPanel />
          </div>

          <KpiStrip stats={stats} />

          <ShopStatusRow
            activeTechnicians={stats?.activeTechnicians ?? 0}
            totalTechnicians={stats?.totalTechnicians ?? 0}
            pendingApprovals={stats?.pendingApprovals ?? 0}
            overdueWorkOrders={stats?.overdueWorkOrders ?? 0}
          />

          <PipelineBar counts={stats?.woCountsByStatus ?? ZERO_PIPELINE} />
        </div>
      </div>
    </div>
  );
};

export default OwnerDashboard;
